import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DateRangeFilter } from '../components/ui/DateRangeFilter';
import { toast } from 'sonner';
import { Plus, ArrowUpCircle, ArrowDownCircle, Wallet, ShoppingCart, Receipt, CreditCard, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { SaleLink, ExpenseLink, ReservationLink } from '../components/crm/EntityLinks';
import useConfig from '../hooks/useConfig';
import { formatCurrency, getFluxInfo, safeFixed } from '../utils/formatters';

export default function CashPage() {
  const [movements, setMovements] = useState([]);
  const [balance, setBalance] = useState({ balance: 0, total_in: 0, total_out: 0 });
  const [periodTotals, setPeriodTotals] = useState({ total_in: 0, total_out: 0 }); // Totaux de la période filtrée
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  
  // Load flux types from config
  const { options: configOptions } = useConfig(['FLUX_TYPE']);
  const fluxTypes = configOptions.FLUX_TYPE || [];
  
  // Get the cash flux code dynamically (first non-SOCIETE flux, or fallback)
  const cashFluxCode = fluxTypes.find(f => f.code !== 'SOCIETE' && f.metadata?.is_cash === true)?.code 
    || fluxTypes.find(f => f.code === 'CASH_CAISSE')?.code 
    || 'CASH_CAISSE';
  
  // Date filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [formData, setFormData] = useState({
    type: 'IN',
    amount: '',
    source: 'DEPOSIT',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, [dateFrom, dateTo, cashFluxCode]);

  const fetchData = async () => {
    try {
      const params = { type_flux: cashFluxCode, limit: 100 };
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      
      // Toujours récupérer le solde actuel (sans filtre) + les mouvements filtrés
      const [movementsRes, currentBalanceRes, periodBalanceRes] = await Promise.all([
        api.get('/cash', { params }),
        api.get('/cash/balance'), // Solde actuel toujours sans filtre
        (dateFrom || dateTo) ? api.get('/cash/balance', { params: { from: dateFrom, to: dateTo } }) : null
      ]);
      
      setMovements(movementsRes.data.data);
      setBalance(currentBalanceRes.data); // Solde actuel toujours le total
      
      // Si on a un filtre de date, afficher les totaux de la période
      if (periodBalanceRes) {
        setPeriodTotals({
          total_in: periodBalanceRes.data.total_in,
          total_out: periodBalanceRes.data.total_out
        });
      } else {
        setPeriodTotals({
          total_in: currentBalanceRes.data.total_in,
          total_out: currentBalanceRes.data.total_out
        });
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/cash', {
        ...formData,
        type_flux: 'CASH_CAISSE'
      });
      toast.success('Mouvement enregistré');
      setShowDialog(false);
      setFormData({ type: 'IN', amount: '', source: 'DEPOSIT', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  return (
    <div className="space-y-6" data-testid="cash-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Fond de caisse</h1>
          <p className="text-muted-foreground mt-1">Suivi des espèces</p>
        </div>
        <Button onClick={() => setShowDialog(true)} data-testid="add-movement-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau mouvement
        </Button>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-metric bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-amber-500/20">
                <Wallet className="w-8 h-8 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-amber-800/70">Solde actuel</p>
                <p className="text-3xl font-bold text-amber-700">{formatCurrency(balance.balance)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-metric">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-green-100">
                <ArrowDownCircle className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {(dateFrom || dateTo) ? 'Entrées (période)' : 'Total entrées'}
                </p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(periodTotals.total_in)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-metric">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-red-100">
                <ArrowUpCircle className="w-8 h-8 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">
                  {(dateFrom || dateTo) ? 'Sorties (période)' : 'Total sorties'}
                </p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(periodTotals.total_out)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Date Filter */}
      <Card className="p-4">
        <DateRangeFilter
          startDate={dateFrom}
          endDate={dateTo}
          onStartDateChange={setDateFrom}
          onEndDateChange={setDateTo}
          onReset={() => { setDateFrom(''); setDateTo(''); }}
          showQuickFilters={true}
        />
      </Card>

      {/* Movements List */}
      <Card>
        <CardHeader>
          <CardTitle>Historique des mouvements</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : movements.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              Aucun mouvement trouvé
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.map((movement) => {
                    // Determine the icon and link based on source
                    const getSourceInfo = () => {
                      switch (movement.source) {
                        case 'SALE':
                          return {
                            icon: <ShoppingCart className="w-3.5 h-3.5" />,
                            label: 'Vente',
                            link: movement.reference_id ? (
                              <SaleLink sale={{ id: movement.reference_id, sale_number: `V-${movement.reference_id}` }} showIcon={false} />
                            ) : null
                          };
                        case 'EXPENSE':
                          return {
                            icon: <Receipt className="w-3.5 h-3.5" />,
                            label: 'Dépense',
                            link: movement.reference_id ? (
                              <ExpenseLink expense={{ id: movement.reference_id, description: movement.description }} showIcon={false} />
                            ) : null
                          };
                        case 'TOURIST_TAX':
                          return {
                            icon: <CreditCard className="w-3.5 h-3.5" />,
                            label: 'Taxe séjour',
                            link: movement.reference_id ? (
                              <ReservationLink reservation={{ id: movement.reference_id }} showIcon={false} />
                            ) : null
                          };
                        case 'RESERVATION':
                          return {
                            icon: <Calendar className="w-3.5 h-3.5" />,
                            label: 'Réservation',
                            link: movement.reference_id ? (
                              <ReservationLink reservation={{ id: movement.reference_id }} showIcon={false} />
                            ) : null
                          };
                        case 'DEPOSIT':
                          return { icon: <ArrowDownCircle className="w-3.5 h-3.5" />, label: 'Dépôt', link: null };
                        case 'WITHDRAWAL':
                          return { icon: <ArrowUpCircle className="w-3.5 h-3.5" />, label: 'Retrait', link: null };
                        case 'ADJUSTMENT':
                          return { icon: <Wallet className="w-3.5 h-3.5" />, label: 'Ajustement', link: null };
                        default:
                          return { icon: null, label: movement.source || '-', link: null };
                      }
                    };
                    
                    const sourceInfo = getSourceInfo();
                    
                    return (
                      <tr key={movement.id} data-testid={`movement-row-${movement.id}`}>
                        <td>{format(new Date(movement.movement_date), 'dd/MM/yyyy HH:mm')}</td>
                        <td>
                          {movement.type === 'IN' ? (
                            <Badge className="bg-green-100 text-green-800">Entrée</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">Sortie</Badge>
                          )}
                        </td>
                        <td>
                          <div className="space-y-1">
                            <div className="flex items-center gap-1.5">
                              {sourceInfo.icon}
                              <span className="text-sm font-medium">{sourceInfo.label}</span>
                              {sourceInfo.link && <span className="text-muted-foreground">-</span>}
                              {sourceInfo.link}
                            </div>
                            {movement.description && movement.description !== sourceInfo.label && (
                              <p className="text-sm text-muted-foreground">{movement.description}</p>
                            )}
                          </div>
                        </td>
                        <td className={`font-semibold ${movement.type === 'IN' ? 'text-green-600' : 'text-red-600'}`}>
                          <div className="text-right">
                            <div>
                              {movement.type === 'IN' ? '+' : '-'}{formatCurrency(movement.amount_mad || movement.amount, 'MAD')}
                            </div>
                            {movement.currency && movement.currency !== 'MAD' && (
                              <div className="text-xs text-muted-foreground font-normal">
                                ({movement.amount} {movement.currency} × {safeFixed(movement.exchange_rate, 4, '?')})
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Movement Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouveau mouvement de caisse</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Type *</Label>
              <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IN">Entrée</SelectItem>
                  <SelectItem value="OUT">Sortie</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Source *</Label>
              <Select value={formData.source} onValueChange={(v) => setFormData({ ...formData, source: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DEPOSIT">Dépôt</SelectItem>
                  <SelectItem value="WITHDRAWAL">Retrait</SelectItem>
                  <SelectItem value="ADJUSTMENT">Ajustement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Montant *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Raison du mouvement"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button type="submit">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
